import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo
} from 'react';
import PropTypes from 'prop-types';
import { Popover, PopoverHeader } from 'reactstrap';
import Select from 'react-select';
import {
  apiErrorHandler,
  cancelAllRequests
} from '../../actions/async_select_actions';

const propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.array.isRequired,
  minLength: PropTypes.number,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  multiple: PropTypes.bool,
  dropup: PropTypes.bool,
  isInvalid: PropTypes.bool,
  itemsPerPage: PropTypes.number,
  filterResults: PropTypes.object,
  placeholder: PropTypes.string,
  paginate: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  getData: PropTypes.func.isRequired,
  onToggleMenu: PropTypes.func,
  clearable: PropTypes.bool
};

const defaultProps = {
  minLength: 0,
  multiple: false,
  dropup: false,
  isInvalid: false,
  readOnly: false,
  disabled: false,
  itemsPerPage: 10,
  filterResults: {},
  placeholder: 'Search...',
  paginate: true,
  onToggleMenu: () => {},
  clearable: true
};

const AsyncSelect = ({
  name,
  value,
  minLength,
  readOnly,
  disabled,
  multiple,
  dropup,
  isInvalid,
  itemsPerPage,
  filterResults,
  placeholder,
  paginate,
  onSelect,
  getData,
  onToggleMenu,
  clearable
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [notSelected, setNotSelected] = useState(false);

  const cacheRef = useRef({});
  const isMountedRef = useRef(true);
  const idSuffix = useRef(new Date().getTime());
  const perPageRef = useRef(itemsPerPage + value.length);
  const debounceTimerRef = useRef(null);
  const inputValueRef = useRef('');

  useEffect(() => {
    isMountedRef.current = true;
    perPageRef.current = itemsPerPage + value.length;
    return () => {
      isMountedRef.current = false;
      cancelAllRequests();
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filterLength = Object.keys(filterResults).length;
  const isInvalidField = isInvalid || notSelected;

  const handleSearch = useCallback(
    query => {
      const cache = cacheRef.current;
      const perPage = perPageRef.current;

      if (!filterLength && cache[query]) {
        cache[query].page = 1;
        setOptions(cache[query].options);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      getData(1, perPage + filterLength, query)
        .then(({ options: newOptions, total_count }) => {
          if (!isMountedRef.current) return;
          cache[query] = { options: newOptions, page: 1, total_count };
          setOptions(newOptions);
          setIsLoading(false);
        })
        .catch(error => {
          apiErrorHandler(error);
          if (isMountedRef.current) setIsLoading(false);
        });
    },
    [getData, filterLength]
  );

  const handlePagination = useCallback(() => {
    if (!paginate) return;

    const query = inputValueRef.current || '';
    const cache = cacheRef.current;
    const cachedQuery = cache[query];

    if (!cachedQuery) return;
    // Don't make another request if we already have all the results.
    if (cachedQuery.options.length >= cachedQuery.total_count) return;

    const page = cachedQuery.page + 1;
    const perPage = perPageRef.current;

    setIsLoading(true);

    getData(page, perPage + filterLength, query)
      .then(res => {
        if (!isMountedRef.current) return;
        const mergedOptions = cachedQuery.options.concat(res.options);
        cache[query] = { ...cachedQuery, options: mergedOptions, page };
        setOptions(mergedOptions);
        setIsLoading(false);
      })
      .catch(error => {
        apiErrorHandler(error);
        if (isMountedRef.current) setIsLoading(false);
      });
  }, [getData, paginate, filterLength]);

  const handleInputChange = useCallback(
    (newValue, actionMeta) => {
      if (actionMeta.action !== 'input-change') return newValue;

      inputValueRef.current = newValue;

      if (newValue.length >= minLength || newValue === '') {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
          handleSearch(newValue);
        }, 300);
      }

      return newValue;
    },
    [handleSearch, minLength]
  );

  const handleChange = useCallback(
    selected => {
      let newValue;
      if (multiple) {
        newValue = selected ? [...selected] : [];
      } else {
        newValue = selected ? [selected] : [];
      }

      perPageRef.current = itemsPerPage + newValue.length;
      delete cacheRef.current[''];

      setNotSelected(false);
      inputValueRef.current = '';

      onSelect({
        target: {
          name,
          value: newValue
        }
      });
    },
    [multiple, itemsPerPage, name, onSelect]
  );

  const handleMenuOpen = useCallback(() => {
    onToggleMenu(true);
    handleSearch('');
  }, [onToggleMenu, handleSearch]);

  const handleMenuClose = useCallback(() => {
    onToggleMenu(false);
  }, [onToggleMenu]);

  const handleBlur = useCallback(() => {
    const currentInput = inputValueRef.current;
    if (!multiple) {
      setNotSelected(!!currentInput && !value.length);
    } else {
      setNotSelected(!!currentInput);
    }
  }, [multiple, value]);

  const handleFocus = useCallback(() => {
    setNotSelected(false);
  }, []);

  // Filter options by filterResults object
  const filteredOptions = useMemo(() => {
    if (!filterLength) return options;
    return options.filter(option => !filterResults[option.id]);
  }, [options, filterResults, filterLength]);

  // Convert value array to the format react-select expects
  const selectValue = useMemo(() => {
    if (multiple) return value;
    return value.length > 0 ? value[0] : null;
  }, [value, multiple]);

  // Custom styles to reflect invalid state and match project theme
  const customStyles = useMemo(
    () => ({
      control: (base, state) => ({
        ...base,
        backgroundColor: '#f2f9fe',
        borderColor: isInvalidField ? '#dc3545' : '#e4e7ea',
        minHeight: 'calc(1.5em + 0.75rem + 2px)',
        '&:hover': {
          borderColor: isInvalidField ? '#dc3545' : '#e4e7ea'
        }
      }),
      singleValue: base => ({ ...base, color: '#5c6873', fontSize: '1rem' }),
      singleValueLabel: base => ({ ...base, fontSize: '1rem' }),
      multiValueLabel: base => ({ ...base, fontSize: '0.8rem' }),
      input: base => ({ ...base, color: '#5c6873' }),
      placeholder: base => ({ ...base, color: '#5c6873', fontSize: '1rem' }),
      menuPortal: base => ({ ...base, zIndex: 9999 })
    }),
    [isInvalidField]
  );

  return (
    <div className="async-select-wrapper">
      <Select
        inputId={`async_select_${idSuffix.current}`}
        className="async-select__container"
        name={name}
        isMulti={multiple}
        isDisabled={disabled || readOnly}
        isSearchable={!readOnly}
        isClearable={clearable}
        isLoading={isLoading}
        value={selectValue}
        placeholder={placeholder}
        menuPlacement={dropup ? 'top' : 'auto'}
        menuPosition="fixed"
        menuPortalTarget={document.body}
        getOptionValue={option => option.id}
        getOptionLabel={option => option.label}
        options={filteredOptions}
        filterOption={null}
        onMenuScrollToBottom={paginate ? handlePagination : undefined}
        onChange={handleChange}
        onInputChange={handleInputChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onMenuOpen={handleMenuOpen}
        onMenuClose={handleMenuClose}
        styles={customStyles}
        classNamePrefix="async-select"
      />
      <div
        id={`async_popover_${idSuffix.current}`}
        className="select-popover-style"
      />
      <Popover
        placement="bottom"
        target={`async_popover_${idSuffix.current}`}
        isOpen={isInvalidField}
        trigger="legacy"
        delay={0}
      >
        <PopoverHeader>Please select a value.</PopoverHeader>
      </Popover>
    </div>
  );
};

AsyncSelect.propTypes = propTypes;
AsyncSelect.defaultProps = defaultProps;
export default AsyncSelect;
